/**
 * Aliyun CDN Image Optimization Utility
 *
 * Uses x-oss-process query parameter to resize images on-the-fly.
 * This reduces bandwidth and improves LCP by serving appropriately sized images.
 *
 * Prerequisites:
 * - CDN must have "Retain specified parameters" set to include "x-oss-process"
 * - OSS bucket must support image processing (enabled by default)
 */

const CDN_DOMAIN = 'videos.littlelambenglish.com';

/**
 * Add Aliyun CDN image processing params to optimize image delivery
 * @param {string} url - Original image URL
 * @param {object} options - Optimization options
 * @param {number} options.width - Target width in pixels
 * @param {number} options.quality - JPEG quality 1-100 (default: 85)
 * @returns {string} Optimized URL with x-oss-process params
 */
export function optimizeImageUrl(url, { width, quality = 85 } = {}) {
  if (!url || !url.includes(CDN_DOMAIN)) {
    return url; // Only process our CDN images
  }

  if (!width) {
    return url; // No optimization requested
  }

  const params = ['image', `resize,w_${width}`];

  // Add quality param for JPEG compression
  if (quality && quality < 100) {
    params.push(`quality,q_${quality}`);
  }

  // WebP: about half the bytes of PNG/JPG, alpha kept (a 53 KB quiz PNG → 14 KB). Every
  // browser our users have shows it — no iOS below 14 in 60 days of pageviews (2026-10-21).
  params.push('format,webp');

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}x-oss-process=${params.join('/')}`;
}

/**
 * Predefined sizes for common use cases
 */
export const IMAGE_SIZES = {
  POSTER: 800,      // Video poster (max-width: 800px)
  THUMBNAIL: 150,   // Drawer/list thumbnails (80-120px display)
  CARD: 400,        // Course cards (max 640px, but 400 is sufficient)
};
